import { LanguagesEnum } from '@/api/enums';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Course, CourseStatusAttemptEnum } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { CourseLanguageChanger } from '@/components/courses/CourseLanguageChanger';
import { useAppSelector } from '@/hooks/store';
import { useLocationState } from '@/hooks/useLocationState';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getTranslatedCourseStatusAttempt,
  getTranslationByLanguage,
} from '@/utils/helpers';
import { isMobile } from '@/utils/helpers/ui.helper';
import { MenuItem } from 'primereact/menuitem';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledAppChip = styled(AppChip)`
  margin-left: var(--small-padding);

  &.mobile .p-chip-text {
    margin: var(--xxsmall-padding);
  }
`;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--small-padding);

  h1 {
    margin-bottom: 0;

    @media screen and (max-width: 767px) {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
    }
  }
`;

const StyledP = styled.p`
  font-size: var(--small-font-size);
  margin: 0;
`;

const getAppChipType = (status: string | undefined) => {
  switch (status) {
    case CourseStatusAttemptEnum.IN_PROGRESS:
      return 'secondary';
    case CourseStatusAttemptEnum.COMPLETED:
      return 'primary';
    case CourseStatusAttemptEnum.FAILED:
      return 'error';
    default:
      return 'secondary';
  }
};

type ContentActionHeaderProps = {
  course: Course;
  status?: CourseStatusAttemptEnum;
  selectedLanguage?: LanguagesEnum;
  setSelectedLanguage: (language: LanguagesEnum) => void;
  isContentStarted?: boolean;
  isDisabled?: boolean;
};

export const ContentActionHeader: React.FC<ContentActionHeaderProps> = ({
  course,
  status,
  selectedLanguage,
  setSelectedLanguage,
  isContentStarted,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { locationFrom } = useLocationState();
  const currentUser = useAppSelector(selectCurrentUser);

  const courseName = getTranslationByLanguage(
    course?.courseTranslations ?? [],
    currentUser?.language
      ? (currentUser?.language as LanguagesEnum)
      : course.defaultLanguage,
    course.defaultLanguage,
  )?.name;

  const getBreadcrumbs = (t: TranslationFunctionType): MenuItem[] => [
    {
      label: t('content'),
      url:
        locationFrom() ||
        RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: courseName,
      url: RedirectPaths[RedirectPathsEnum.CONTENT_INFO](course?.id ?? ''),
      template: AppBreadCrumbTemplate,
      data: { state: location?.state },
    },
  ];

  const navigateToCourseInfo = async () => {
    if (course) {
      navigate(RedirectPaths[RedirectPathsEnum.CONTENT_INFO](course?.id), {
        state: location?.state,
      });
    }
  };

  return (
    <>
      <AppBreadCrumb model={getBreadcrumbs(t)} className="no-space" />
      <FlexContainer justify="space-between" wrap="wrap">
        <StyledDiv>
          <AppButton
            type="outlined"
            icon="pi pi-arrow-left"
            size={isMobile() ? 'xs' : undefined}
            ariaLabel={t('course.player.back')}
            isDisabled={isDisabled}
            onClick={
              isMobile()
                ? () =>
                    navigate(
                      locationFrom() ||
                        RedirectPaths[
                          RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE
                        ](),
                    )
                : navigateToCourseInfo
            }
          />
          <h1 aria-describedby="course-status course-requirements">
            {courseName}
          </h1>
          <StyledAppChip
            id="course-status"
            className={isMobile() ? 'mobile' : ''}
            key={status}
            label={getTranslatedCourseStatusAttempt(status)}
            type={getAppChipType(status)}
          />
          {!course.exemptFromMinimumRequirementScore &&
            !!currentUser?.account?.courseMinimumScore && (
              <StyledP id="course-requirements">
                {t('content.minimum.score.to.complete.this.content', {
                  percent: currentUser.account.courseMinimumScore,
                })}
              </StyledP>
            )}
        </StyledDiv>
        <StyledDiv>
          {isContentStarted &&
            course.selfHosted &&
            course?.courseLanguages?.length > 1 &&
            !isMobile() && (
              <CourseLanguageChanger
                language={selectedLanguage}
                course={course}
                shouldShowWarning={true}
                isDisabled={isDisabled}
                onChange={(value) => setSelectedLanguage(value)}
              />
            )}

          {isContentStarted && !isMobile() && (
            <AppButton
              type="outlined"
              icon="pi pi-times"
              size={isMobile() ? 'xs' : undefined}
              ariaLabel={t('content.back')}
              isDisabled={isDisabled}
              onClick={() =>
                navigate(
                  locationFrom() ||
                    RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](),
                )
              }
            />
          )}
        </StyledDiv>
      </FlexContainer>
    </>
  );
};
